import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  Button,
  Card,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { Iconify } from 'src/components/iconify';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionButton } from 'src/routes/components';

import { useCopyFaq } from './hooks/useCopyFaq';
import type {
  CopyPanelProps,
  EventCopy,
  FaqDraft,
  FaqPanelProps,
  SaveBarProps,
  TranslateFn,
} from './types';
import {
  buildPayload,
  createEmptyFaq,
  getDefaultCopy,
  getErrorMessage,
  isBundleEqual,
  toDraftFaqs,
} from './utils';

const I18N_PREFIX = 'module.worldCup.copyFaq';

const tCopyFaq = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const { copy, faqs, loading, saving, refetch, replaceBundle } = useCopyFaq();

  const [copyDraft, setCopyDraft] = useState<EventCopy>(getDefaultCopy());
  const [originalCopyDraft, setOriginalCopyDraft] = useState<EventCopy>(getDefaultCopy());
  const [faqDrafts, setFaqDrafts] = useState<FaqDraft[]>([]);
  const [originalFaqDrafts, setOriginalFaqDrafts] = useState<FaqDraft[]>([]);

  useEffect(() => {
    const nextFaqs = toDraftFaqs(faqs);

    setCopyDraft(copy);
    setOriginalCopyDraft(copy);
    setFaqDrafts(nextFaqs);
    setOriginalFaqDrafts(nextFaqs);
  }, [copy, faqs]);

  const hasUnsavedChanges = useMemo(
    () => !isBundleEqual(copyDraft, faqDrafts, originalCopyDraft, originalFaqDrafts),
    [copyDraft, faqDrafts, originalCopyDraft, originalFaqDrafts]
  );

  const handleCopyChange = useCallback((patch: Partial<EventCopy>) => {
    setCopyDraft((prev) => ({
      ...prev,
      ...patch,
    }));
  }, []);

  const handleDiscard = useCallback(() => {
    if (!window.confirm(tCopyFaq(t, 'confirm.discard'))) return;

    setCopyDraft(originalCopyDraft);
    setFaqDrafts(originalFaqDrafts);
  }, [originalCopyDraft, originalFaqDrafts, t]);

  const handleSave = useCallback(async () => {
    const copyInvalid =
      !copyDraft.bannerZh.trim() ||
      !copyDraft.bannerEn.trim() ||
      !copyDraft.taglineZh.trim() ||
      !copyDraft.taglineEn.trim();

    if (copyInvalid) {
      notify(tCopyFaq(t, 'notify.copyRequired'), { type: 'error' });
      return;
    }

    const faqInvalid = faqDrafts.some(
      (row) =>
        !row.questionZh.trim() ||
        !row.questionEn.trim() ||
        !row.answerZh.trim() ||
        !row.answerEn.trim()
    );

    if (faqInvalid) {
      notify(tCopyFaq(t, 'notify.faqRequired'), { type: 'error' });
      return;
    }

    try {
      await replaceBundle(buildPayload(copyDraft, faqDrafts));
      await refetch();

      notify(tCopyFaq(t, 'notify.saveSuccess'), { type: 'success' });
    } catch (err) {
      notify(getErrorMessage(err) || tCopyFaq(t, 'notify.saveFailed'), { type: 'error' });
    }
  }, [copyDraft, faqDrafts, notify, refetch, replaceBundle, t]);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tCopyFaq(t, 'title')}
        links={[{ name: t('resource.worldCup') }, { name: t('resource.worldCupFaq') }]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tCopyFaq(t, 'description')}
      </Typography>

      {loading ? (
        <CenterBox>
          <CircularProgress size={28} />
        </CenterBox>
      ) : (
        <Stack spacing={3}>
          <CopyPanel copy={copyDraft} onChange={handleCopyChange} />
          <FaqPanel rows={faqDrafts} onRowsChange={setFaqDrafts} />
        </Stack>
      )}

      {hasUnsavedChanges ? (
        <SaveBar loading={saving} onDiscard={handleDiscard} onSave={handleSave} />
      ) : null}
    </DashboardContent>
  );
};

const CopyPanel = memo(({ copy, onChange }: CopyPanelProps) => {
  const t = useTranslate();

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6">{tCopyFaq(t, 'copy.title')}</Typography>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label={tCopyFaq(t, 'copy.bannerZh')}
            size="small"
            fullWidth
            required
            value={copy.bannerZh}
            error={!copy.bannerZh.trim()}
            helperText={!copy.bannerZh.trim() ? t('ra.validation.required') : undefined}
            onChange={(event) => onChange({ bannerZh: event.target.value })}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            label={tCopyFaq(t, 'copy.bannerEn')}
            size="small"
            fullWidth
            required
            value={copy.bannerEn}
            error={!copy.bannerEn.trim()}
            helperText={!copy.bannerEn.trim() ? t('ra.validation.required') : undefined}
            onChange={(event) => onChange({ bannerEn: event.target.value })}
            inputProps={{ maxLength: 255 }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label={tCopyFaq(t, 'copy.taglineZh')}
            size="small"
            fullWidth
            required
            multiline
            minRows={3}
            value={copy.taglineZh}
            error={!copy.taglineZh.trim()}
            helperText={!copy.taglineZh.trim() ? t('ra.validation.required') : undefined}
            onChange={(event) => onChange({ taglineZh: event.target.value })}
            inputProps={{ maxLength: 500 }}
          />
          <TextField
            label={tCopyFaq(t, 'copy.taglineEn')}
            size="small"
            fullWidth
            required
            multiline
            minRows={3}
            value={copy.taglineEn}
            error={!copy.taglineEn.trim()}
            helperText={!copy.taglineEn.trim() ? t('ra.validation.required') : undefined}
            onChange={(event) => onChange({ taglineEn: event.target.value })}
            inputProps={{ maxLength: 500 }}
          />
        </Stack>
      </Stack>
    </Card>
  );
});

CopyPanel.displayName = 'CopyPanel';

const FaqPanel = memo(({ rows, onRowsChange }: FaqPanelProps) => {
  const t = useTranslate();

  const handleAdd = () => {
    const maxSortOrder = rows.reduce((max, item) => Math.max(max, item.sortOrder ?? 0), -1);
    onRowsChange([...rows, createEmptyFaq(maxSortOrder + 1)]);
  };

  const displayRows = useMemo(
    () => rows.slice().sort((a, b) => (b.sortOrder ?? 0) - (a.sortOrder ?? 0)),
    [rows]
  );

  const handleRemove = (localId: string) => {
    onRowsChange(rows.filter((item) => item.localId !== localId));
  };

  const handleRowChange = (localId: string, patch: Partial<FaqDraft>) => {
    onRowsChange(rows.map((item) => (item.localId === localId ? { ...item, ...patch } : item)));
  };

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Stack>
          <Typography variant="h6">{tCopyFaq(t, 'faq.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {tCopyFaq(t, 'faq.desc')}
          </Typography>
        </Stack>

        <Button variant="outlined" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleAdd}>
          {tCopyFaq(t, 'actions.addFaq')}
        </Button>
      </Stack>

      {displayRows.length ? (
        <Stack spacing={2}>
          {displayRows.map((row) => (
            <FaqCard
              key={row.localId}
              row={row}
              index={row.sortOrder ?? 0}
              onChange={(patch) => handleRowChange(row.localId, patch)}
              onRemove={() => handleRemove(row.localId)}
            />
          ))}
        </Stack>
      ) : (
        <EmptyText text={tCopyFaq(t, 'empty.faq')} />
      )}
    </Card>
  );
});

FaqPanel.displayName = 'FaqPanel';

const FaqCard = memo(
  ({
    row,
    index,
    onChange,
    onRemove,
  }: {
    row: FaqDraft;
    index: number;
    onChange: (patch: Partial<FaqDraft>) => void;
    onRemove: () => void;
  }) => {
    const t = useTranslate();

    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Chip label={`Q${index + 1}`} color="primary" size="small" />

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {tCopyFaq(t, 'faq.visible')}
            </Typography>

            <Switch
              checked={row.visible}
              onChange={(_, checked) => onChange({ visible: checked })}
            />

            <PermissionButton resource="world-cup-copy-faq" action="write">
              <Button variant="text" color="error" onClick={onRemove}>
                {tCopyFaq(t, 'actions.delete')}
              </Button>
            </PermissionButton>
          </Stack>
        </Stack>

        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label={tCopyFaq(t, 'faq.questionZh')}
              size="small"
              fullWidth
              required
              value={row.questionZh}
              error={!row.questionZh.trim()}
              helperText={!row.questionZh.trim() ? t('ra.validation.required') : undefined}
              onChange={(event) => onChange({ questionZh: event.target.value })}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label={tCopyFaq(t, 'faq.questionEn')}
              size="small"
              fullWidth
              required
              value={row.questionEn}
              error={!row.questionEn.trim()}
              helperText={!row.questionEn.trim() ? t('ra.validation.required') : undefined}
              onChange={(event) => onChange({ questionEn: event.target.value })}
              inputProps={{ maxLength: 255 }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label={tCopyFaq(t, 'faq.answerZh')}
              size="small"
              fullWidth
              required
              multiline
              minRows={4}
              value={row.answerZh}
              error={!row.answerZh.trim()}
              helperText={!row.answerZh.trim() ? t('ra.validation.required') : undefined}
              onChange={(event) => onChange({ answerZh: event.target.value })}
              inputProps={{ maxLength: 5000 }}
            />
            <TextField
              label={tCopyFaq(t, 'faq.answerEn')}
              size="small"
              fullWidth
              required
              multiline
              minRows={4}
              value={row.answerEn}
              error={!row.answerEn.trim()}
              helperText={!row.answerEn.trim() ? t('ra.validation.required') : undefined}
              onChange={(event) => onChange({ answerEn: event.target.value })}
              inputProps={{ maxLength: 5000 }}
            />
          </Stack>
        </Stack>
      </Paper>
    );
  }
);

FaqCard.displayName = 'FaqCard';

const SaveBar = memo(({ loading, onDiscard, onSave }: SaveBarProps) => {
  const t = useTranslate();

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'sticky',
        bottom: 16,
        mt: 3,
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Stack>
        <Typography variant="subtitle1">{tCopyFaq(t, 'saveBar.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {tCopyFaq(t, 'saveBar.desc')}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" disabled={loading} onClick={onDiscard}>
          {tCopyFaq(t, 'actions.discard')}
        </Button>

        <PermissionButton resource="world-cup-copy-faq" action="write">
          <Button variant="contained" disabled={loading} onClick={onSave}>
            {loading ? t('common.saving') : tCopyFaq(t, 'actions.save')}
          </Button>
        </PermissionButton>
      </Stack>
    </Paper>
  );
});

SaveBar.displayName = 'SaveBar';

const EmptyText = memo(({ text }: { text: string }) => (
  <Stack alignItems="center" sx={{ py: 4 }}>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Stack>
));

EmptyText.displayName = 'EmptyText';

const CenterBox = ({ children }: { children: ReactNode }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
    {children}
  </Stack>
);
